<template>
  <div class="realname-page" :class="{ embedded: embedded }">
    <div class="container">
      <h2 v-if="!embedded" class="page-title">实名认证 · 青少年模式</h2>

      <div v-if="!token" class="login-tip">
        <p>请先 <router-link to="/login">登录</router-link> 后进行实名认证。</p>
      </div>

      <template v-else>
        <!-- 状态概览 -->
        <div class="status-card">
          <div class="status-header">
            <div class="status-icon" :class="realnameClass">
              <svg v-if="status.realname_status === 1" viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
              </svg>
              <svg v-else-if="status.realname_status === 2" viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
              </svg>
              <svg v-else viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
            </div>
            <div class="status-main">
              <div class="status-title" :class="realnameClass">{{ realnameText }}</div>
              <div class="status-sub">
                <template v-if="status.realname_status === 1">{{ status.is_minor ? '未成年 · 已受保护' : '成年 · 认证通过' }}</template>
                <template v-else-if="status.realname_status === 2">实名信息已提交，平台审核中</template>
                <template v-else>完成实名认证，享受完整服务</template>
              </div>
            </div>
            <div class="mode-switch">
              <span class="mode-label">青少年模式</span>
              <button class="mode-btn" :class="status.minor_mode ? 'on' : 'off'" :disabled="!token || submitting" @click="toggleMode">
                {{ status.minor_mode ? '已开启' : '开启' }}
              </button>
              <span v-if="modeLocked" class="mode-lock">强制生效</span>
            </div>
          </div>
          <div class="status-rows">
            <div class="status-row">
              <span class="label">是否未成年</span>
              <span class="value" :class="status.is_minor ? 'minor' : 'ok'">
                {{ status.realname_status === 1 ? (status.is_minor ? '未成年' : '成年') : '未实名' }}
              </span>
            </div>
            <div class="status-row" v-if="status.realname_status === 1">
              <span class="label">姓名 / 年龄</span>
              <span class="value">{{ status.realname }} · {{ status.age }}岁</span>
            </div>
            <div class="status-row" v-if="status.verify_remark">
              <span class="label">审核备注</span>
              <span class="value warn">{{ status.verify_remark }}</span>
            </div>
            <div class="status-row">
              <span class="label">青少年模式</span>
              <span class="value" :class="status.minor_mode ? 'warn' : 'ok'">
                {{ status.minor_mode ? (modeLocked ? '已开启 · 强制生效' : '已开启') : '已关闭' }}
              </span>
            </div>
            <div class="status-row">
              <span class="label">今日剩余可用</span>
              <span class="value" :class="status.usage && status.usage.locked ? 'minor' : 'warn'">
                {{ remainText }}
              </span>
            </div>
          </div>
        </div>

        <!-- 认证表单 -->
        <div class="form-card" v-if="status.realname_status === 0">
          <h3 class="section-title">实名认证</h3>
          <div class="form-group">
            <label>真实姓名</label>
            <input type="text" v-model="form.name" placeholder="请输入与身份证一致的姓名" maxlength="30">
          </div>
          <div class="form-group">
            <label>身份证号</label>
            <input type="text" v-model="form.idcard" placeholder="请输入18位身份证号" maxlength="18">
          </div>
          <button class="submit-btn" :disabled="submitting" @click="handleSubmit">
            {{ submitting ? '认证中...' : '提交认证' }}
          </button>
          <p class="hint">根据《未成年人网络保护条例》，未成年人充值、打赏将受宵禁与限额限制。身份证号仅做本地校验与加密存储。提交后需平台审核，请耐心等待。</p>
        </div>

        <!-- 待审核提示 -->
        <div class="form-card pending-card" v-if="status.realname_status === 2">
          <h3 class="section-title">认证审核中</h3>
          <p class="pending-tip">您的实名信息已提交，平台正在审核中（通常 1-2 个工作日）。审核通过后即可使用完整功能。</p>
        </div>

        <!-- 青少年模式设置 -->
        <div class="form-card teen-card">
          <h3 class="section-title">青少年模式设置</h3>
          <p v-if="status.realname_status !== 1" class="hint" style="margin-bottom:12px;color:#fa5;">当前账号未完成实名认证，按未成年人对待，青少年模式已强制生效：每日使用时长、宵禁时段与消费限额均已启用，且无法自行关闭。完成实名认证并确认为成年人后，方可自行选择是否关闭。</p>

          <!-- 每日使用时长 -->
          <div class="setting-row">
            <span class="setting-label">每日使用时长</span>
            <b class="setting-value">{{ dailyLimitMin }} 分钟</b>
          </div>
          <button class="minor-action" :disabled="!token" @click="onEditDailyLimit">修改每日时长</button>
          <div v-if="showDailyLimitForm" class="minor-form">
            <input type="number" v-model="dailyLimitInput" min="1" max="1440" placeholder="1-1440 分钟">
            <input type="password" v-model="dailyPwd" placeholder="家长密码">
            <div class="minor-form-actions">
              <button class="submit-btn small" @click="saveDailyLimit">保存</button>
              <button class="cancel-btn" @click="showDailyLimitForm = false">取消</button>
            </div>
          </div>

          <!-- 家长密码 -->
          <div class="setting-row">
            <span class="setting-label">家长密码</span>
            <b class="setting-value" :class="status.has_parent_pwd ? 'ok' : 'warn'">{{ status.has_parent_pwd ? '已设置' : '未设置' }}</b>
          </div>
          <button class="minor-action" :disabled="!token" @click="showParentPwdForm = !showParentPwdForm">
            {{ status.has_parent_pwd ? '修改家长密码' : '设置家长密码' }}
          </button>
          <div v-if="showParentPwdForm" class="minor-form">
            <input type="password" v-model="parentPwd" placeholder="新家长密码（6-30 位）">
            <input type="password" v-model="parentPwdConfirm" placeholder="确认新家长密码">
            <input v-if="status.has_parent_pwd" type="password" v-model="parentOldPwd" placeholder="原家长密码">
            <div class="minor-form-actions">
              <button class="submit-btn small" @click="saveParentPwd">保存</button>
              <button class="cancel-btn" @click="showParentPwdForm = false">取消</button>
            </div>
          </div>
          <p class="hint">家长密码独立于登录密码，用于关闭青少年模式、修改每日使用时长，防止未成年人自行绕过保护。</p>
        </div>

        <!-- 规则说明 -->
        <div class="rules-card">
          <h3 class="section-title">未成年保护规则</h3>
          <ul class="rules-list">
            <li>宵禁时段：每日 <b>{{ cfg.night_start }}:00 - {{ cfg.night_end }}:00</b>（当前{{ cfg.in_curfew ? '已进入宵禁' : '非宵禁时段' }}）</li>
            <li>单笔消费上限：<b>￥{{ cfg.single_limit }}</b></li>
            <li>单日消费上限：<b>￥{{ cfg.daily_limit }}</b></li>
            <li v-if="cfg.force_mode">已开启强制青少年模式：未成年用户默认进入青少年模式，不可充值/打赏。</li>
          </ul>
        </div>
      </template>
    </div>
    <PageFooter v-if="!embedded" />

    <!-- 家长密码拦截弹窗（关闭青少年模式时使用） -->
    <ParentPwdModal
      :visible="pwdModal.visible"
      :title="pwdModal.title"
      :placeholder="pwdModal.placeholder"
      :need-confirm="pwdModal.needConfirm"
      @confirm="onPwdConfirm"
      @cancel="onPwdCancel"
    />
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue';
import PageFooter from './Footer.vue';
import ParentPwdModal from './ParentPwdModal.vue';
import { confirmDialog } from './ConfirmDialog.vue';
import { getRealnameStatus, submitRealname, setMinorMode, setParentPwd, setDailyLimit } from '../api/realname';

export default {
  name: 'RealnamePage',
  components: { PageFooter, ParentPwdModal },
  props: {
    embedded: { type: Boolean, default: false }
  },
  setup(props) {
    void props.embedded;
    const token = ref(localStorage.getItem('token'));
    const submitting = ref(false);
    const status = reactive({
      realname_status: 0,
      is_minor: 0,
      minor_mode: 0,
      birthday: '',
      realname: '',
      age: 0,
      has_parent_pwd: 0,
      daily_use_limit_min: 40,
      usage: null
    });
    const cfg = reactive({
      night_start: 22,
      night_end: 8,
      single_limit: 50,
      daily_limit: 100,
      in_curfew: false,
      force_mode: 0
    });
    const form = reactive({ name: '', idcard: '' });

    const loadStatus = async () => {
      if (!token.value) return;
      try {
        const res = await getRealnameStatus();
        if (res.code === 1) {
          const d = res.data;
          Object.assign(status, d);
          if (d.config) Object.assign(cfg, d.config);
        }
      } catch (e) {
        console.error('获取实名状态失败', e);
      }
    };

    const handleSubmit = async () => {
      if (!form.name.trim() || !form.idcard.trim()) {
        alert('请填写姓名和身份证号');
        return;
      }
      submitting.value = true;
      try {
        const res = await submitRealname({ name: form.name.trim(), idcard: form.idcard.trim() });
        if (res.code === 1) {
          alert('已提交，等待审核');
          Object.assign(status, res.data);
          if (res.data.config) Object.assign(cfg, res.data.config);
          form.name = '';
          form.idcard = '';
          window.dispatchEvent(new Event('minor-mode-changed'));
        } else {
          alert(res.data.msg || '认证失败');
        }
      } catch (e) {
        alert((e && e.message) || '认证失败');
      } finally {
        submitting.value = false;
      }
    };

    // 关闭青少年模式：需先验证家长密码
    // 家长密码弹窗：action='setup-enable' 设置密码并开启 / action='close' 验证密码后关闭
    const pwdModal = reactive({
      visible: false,
      title: '请输入家长密码以关闭青少年模式',
      placeholder: '家长密码',
      action: '',
      needConfirm: false
    });

    const toggleMode = async () => {
      if (!token.value) {
        window.location.hash = '#/login';
        return;
      }
      // 防重：请求进行中忽略再次点击，否则连点会发出多个请求、弹出多条重复提示
      if (submitting.value) return;
      const on = status.minor_mode ? 0 : 1;
      if (on === 0) {
        // 已实名未成年：任何情况不可关闭
        if (status.is_minor === 1) {
          alert('实名信息显示为未成年人，青少年模式无法关闭。');
          return;
        }
        if (!status.has_parent_pwd) {
          // 兜底：正常流程开启时已强制设置密码，不应走到这里
          alert('请先设置家长密码');
          return;
        }
        // 关闭：一律验证家长密码（后端同样校验），验证通过才关闭
        pwdModal.action = 'close';
        pwdModal.title = '请输入家长密码以关闭青少年模式';
        pwdModal.placeholder = '家长密码';
        pwdModal.needConfirm = false;
        pwdModal.visible = true;
        return;
      }
      if (!status.has_parent_pwd) {
        // 尚未设置家长密码：先设置（两次输入确认），成功后才真正开启
        pwdModal.action = 'setup-enable';
        pwdModal.title = '设置家长密码';
        pwdModal.placeholder = '设置家长密码（6-30 位）';
        pwdModal.needConfirm = true;
        pwdModal.visible = true;
        return;
      }
      // 已设置家长密码：弹确认框，确认后开启
      const ok = await confirmDialog({
        title: '开启青少年模式',
        message: '确认要开启青少年模式吗？开启后将限制每日使用时长、宵禁时段暂停使用，并仅展示适龄免费内容。',
        okText: '确认开启',
        cancelText: '再想想'
      });
      if (!ok) return;
      await enableMode('已开启青少年模式');
    };

    // 开启青少年模式：仅在确认 / 设置密码完成后调用。
    // 状态只在接口成功后更新，失败或取消均保持原状（UI 与实际一致）。
    const enableMode = async (successMsg) => {
      submitting.value = true;
      try {
        const res = await setMinorMode(1);
        if (res.code === 1) {
          Object.assign(status, res.data);
          if (res.data.config) Object.assign(cfg, res.data.config);
          alert(successMsg || '已开启青少年模式');
          window.dispatchEvent(new Event('minor-mode-changed'));
        } else {
          alert(res.msg || '操作失败');
        }
      } catch (e) {
        alert((e && e.message) || '操作失败');
      } finally {
        submitting.value = false;
      }
    };

    const onPwdConfirm = async (pwd) => {
      if (pwdModal.action === 'setup-enable') {
        // 开启前的密码设置：成功后自动继续开启；失败则状态不变、不会开启
        pwdModal.visible = false;
        try {
          const res = await setParentPwd(pwd);
          if (res.code === 1) {
            status.has_parent_pwd = 1;
            await enableMode('家长密码已设置，青少年模式已开启');
          } else {
            alert(res.msg || '密码设置失败');
          }
        } catch (e) {
          alert((e && e.message) || '密码设置失败');
        }
        return;
      }
      if (pwdModal.action === 'close') {
        // 关闭验证：密码错误时给出明确提示，开关状态保持不变
        pwdModal.visible = false;
        submitting.value = true;
        try {
          const res = await setMinorMode(0, pwd);
          if (res.code === 1) {
            Object.assign(status, res.data);
            if (res.data.config) Object.assign(cfg, res.data.config);
            alert('已关闭青少年模式');
            window.dispatchEvent(new Event('minor-mode-changed'));
          } else {
            alert(res.msg || '家长密码错误');
          }
        } catch (e) {
          alert((e && e.message) || '家长密码错误');
        } finally {
          submitting.value = false;
        }
      }
    };

    const onPwdCancel = () => {
      pwdModal.visible = false;
    };

    // 每日使用时长（有效值）
    const dailyLimitMin = computed(() => {
      if (status.usage && status.usage.daily_use_limit_min) return status.usage.daily_use_limit_min;
      if (status.daily_use_limit_min > 0) return status.daily_use_limit_min;
      return cfg.daily_use_limit_min || 40;
    });

    // 修改每日使用时长（需家长密码）
    const showDailyLimitForm = ref(false);
    const dailyLimitInput = ref(40);
    const dailyPwd = ref('');

    const onEditDailyLimit = () => {
      if (!status.has_parent_pwd) {
        alert('请先设置家长密码');
        return;
      }
      dailyLimitInput.value = dailyLimitMin.value;
      dailyPwd.value = '';
      showDailyLimitForm.value = true;
    };

    const saveDailyLimit = async () => {
      const m = parseInt(dailyLimitInput.value, 10);
      if (!m || m < 1 || m > 1440) {
        alert('每日时长需在 1-1440 分钟之间');
        return;
      }
      if (!dailyPwd.value) {
        alert('请输入家长密码');
        return;
      }
      try {
        const res = await setDailyLimit(m, dailyPwd.value);
        if (res.code === 1) {
          Object.assign(status, res.data);
          if (res.data.config) Object.assign(cfg, res.data.config);
          showDailyLimitForm.value = false;
          alert('每日使用时长已更新');
        } else {
          alert(res.data.msg || '保存失败');
        }
      } catch (e) {
        alert((e && e.message) || '保存失败');
      }
    };

    // 家长密码（独立密码）
    const showParentPwdForm = ref(false);
    const parentPwd = ref('');
    const parentPwdConfirm = ref('');
    const parentOldPwd = ref('');

    const saveParentPwd = async () => {
      if (parentPwd.value.length < 6) {
        alert('家长密码需至少 6 位');
        return;
      }
      if (parentPwd.value !== parentPwdConfirm.value) {
        alert('两次输入的密码不一致');
        return;
      }
      try {
        const res = await setParentPwd(parentPwd.value, parentOldPwd.value);
        if (res.code === 1) {
          alert('家长密码已保存');
          showParentPwdForm.value = false;
          parentPwd.value = '';
          parentPwdConfirm.value = '';
          parentOldPwd.value = '';
          await loadStatus();
        } else {
          alert(res.data.msg || '保存失败');
        }
      } catch (e) {
        alert((e && e.message) || '保存失败');
      }
    };

    // 今日剩余可用时长（与右下角悬浮倒计时同源，避免两处显示不一致）
    const remainText = computed(() => {
      if (!status.minor_mode) return '不限制';
      const u = status.usage;
      if (!u) return dailyLimitMin.value + ' 分钟';
      if (u.locked) return '已用完（需家长密码临时使用）';
      const s = Math.max(0, u.remain_seconds || 0);
      const m = Math.floor(s / 60);
      const sec = s % 60;
      return m + ' 分 ' + String(sec).padStart(2, '0') + ' 秒';
    });

    // 强制生效：直接采用后端 minor_locked 字段（现仅"已实名且判定为未成年"），
    // 未实名用户不再强制开启，可自由开关
    const modeLocked = computed(() => status.minor_locked === 1);

    const realnameText = computed(() => {
      switch (status.realname_status) {
        case 1: return '已认证';
        case 2: return '待审核';
        case 3: return '已拒绝';
        default: return '未认证';
      }
    });
    const realnameClass = computed(() => {
      switch (status.realname_status) {
        case 1: return 'ok';
        case 2: return 'warn';
        case 3: return 'danger';
        default: return 'warn';
      }
    });

    onMounted(loadStatus);

    return {
      token, submitting, status, cfg, form, handleSubmit, toggleMode,
      realnameText, realnameClass,
      pwdModal, onPwdConfirm, onPwdCancel,
      modeLocked, remainText,
      dailyLimitMin, showDailyLimitForm, dailyLimitInput, dailyPwd, onEditDailyLimit, saveDailyLimit,
      showParentPwdForm, parentPwd, parentPwdConfirm, parentOldPwd, saveParentPwd
    };
  }
};
</script>

<style scoped>
.realname-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: #f6f7f9;
  padding: 40px 0;
}
.realname-page.embedded {
  min-height: auto;
  padding: 20px 24px;
  background: transparent;
}
.realname-page .container {
  flex: 1;
  max-width: 720px;
  margin: 0 auto;
  padding: 0 20px;
}
.realname-page.embedded .container {
  max-width: none;
  margin: 0;
  padding: 0;
}
/* 内嵌到个人中心时，父容器已是白底卡片，这里用描边区分分组 */
.realname-page.embedded .status-card,
.realname-page.embedded .form-card,
.realname-page.embedded .rules-card {
  border: 1px solid #e8ecf1;
  background: #fcfdfe;
  box-shadow: none;
  padding: 20px;
}
.realname-page.embedded .status-card,
.realname-page.embedded .form-card {
  margin-bottom: 12px;
}
.realname-page.embedded .rules-card:last-child {
  margin-bottom: 0;
}
.realname-page.embedded .pending-card {
  background: #fff7ed;
  border-color: #fed7aa;
}
.page-title {
  font-size: 24px;
  color: #1a1a1a;
  margin-bottom: 30px;
  font-weight: 600;
}
.login-tip {
  background: #fff;
  border-radius: 12px;
  padding: 40px;
  text-align: center;
  color: #666;
}
.login-tip a {
  color: #ff9600;
  text-decoration: none;
}
.status-card,
.form-card,
.rules-card {
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
}
.status-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding-bottom: 20px;
  margin-bottom: 16px;
  border-bottom: 1px solid #f0f0f0;
}
.status-icon {
  width: 58px;
  height: 58px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: #f6f7f9;
  color: #999;
}
.status-icon.ok { background: #dcfce7; color: #16a34a; }
.status-icon.warn { background: #fef9c3; color: #ca8a04; }
.status-icon.danger { background: #fee2e2; color: #dc2626; }
.status-main { flex: 1; }
.status-title {
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 4px;
}
.status-title.ok { color: #16a34a; }
.status-title.warn { color: #ca8a04; }
.status-title.danger { color: #dc2626; }
.status-sub {
  font-size: 13px;
  color: #999;
}
.status-rows {}
.status-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid #f6f7f9;
}
.status-row:last-child {
  border-bottom: none;
}
.label {
  color: #666;
  font-size: 15px;
}
.value {
  font-size: 15px;
  font-weight: 500;
}
.value.ok { color: #16a34a; }
.value.warn { color: #ca8a04; }
.value.minor { color: #dc2626; }
.mode-switch {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}
.mode-label {
  font-size: 12px;
  color: #888;
  font-weight: 500;
}
.mode-btn {
  min-width: 96px;
  height: 34px;
  padding: 0 20px;
  border: none;
  border-radius: 17px;
  font-size: 14px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  transition: all 0.3s;
}
.mode-btn.on { background: #ff9600; color: #fff; }
.mode-btn.off { background: #e5e5e5; color: #666; }
.mode-lock {
  font-size: 11px;
  color: #dc2626;
  background: #fee2e2;
  border-radius: 8px;
  padding: 2px 8px;
  line-height: 1.6;
}
.section-title {
  font-size: 18px;
  color: #1a1a1a;
  margin: 0 0 20px;
  font-weight: 600;
}
.form-group {
  margin-bottom: 20px;
}
.form-group label {
  display: block;
  margin-bottom: 8px;
  color: #475569;
  font-size: 14px;
}
.form-group input {
  width: 100%;
  padding: 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  background: #f8fafc;
  outline: none;
  box-sizing: border-box;
}
.form-group input:focus {
  border-color: #ff9600;
  background: #fff;
}
.submit-btn {
  min-width: 180px;
  height: 46px;
  padding: 0 36px;
  background: #ff9600;
  color: #fff;
  border: none;
  border-radius: 23px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  transition: all 0.3s;
}
.submit-btn:hover:not(:disabled) {
  background: #ff7d00;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(255, 150, 0, 0.25);
}
.submit-btn.small {
  min-width: 0;
  width: auto;
  height: 38px;
  padding: 0 26px;
  font-size: 14px;
  border-radius: 19px;
}
.submit-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
}
.cancel-btn {
  height: 38px;
  padding: 0 26px;
  background: #f1f5f9;
  color: #475569;
  border: none;
  border-radius: 19px;
  font-size: 14px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}
.hint {
  margin-top: 14px;
  font-size: 13px;
  color: #94a3b8;
  line-height: 1.6;
}
.rules-list {
  margin: 0;
  padding-left: 20px;
  color: #475569;
  line-height: 2;
  font-size: 14px;
}
.rules-list b {
  color: #ff9600;
}

/* 青少年模式设置 */
.teen-card {
  padding-bottom: 12px;
}
.setting-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid #f0f0f0;
  font-size: 15px;
  color: #475569;
}
.setting-value {
  font-weight: 600;
  color: #1a1a1a;
}
.setting-value.ok { color: #16a34a; }
.setting-value.warn { color: #eab308; }
.minor-action {
  margin-top: 14px;
  height: 38px;
  padding: 0 24px;
  background: #fff;
  border: 1px solid #ff9600;
  color: #ff9600;
  border-radius: 19px;
  font-size: 14px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  transition: all 0.3s;
}
.minor-action:hover {
  background: #ff9600;
  color: #fff;
}
.minor-action:disabled,
.mode-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  border-color: #ccc;
  color: #999;
  background: #f5f5f5;
}
.minor-form {
  margin-top: 12px;
  padding: 16px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
}
.minor-form input {
  width: 100%;
  padding: 10px 12px;
  margin-bottom: 10px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  box-sizing: border-box;
  outline: none;
}
.minor-form input:focus {
  border-color: #ff9600;
}
.minor-form-actions {
  display: flex;
  gap: 12px;
  margin-top: 4px;
}
.pending-card {
  background: #fff7ed;
  border: 1px solid #fed7aa;
}
.pending-tip {
  color: #b45309;
  font-size: 14px;
  line-height: 1.7;
}
</style>
